import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect, Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import routeConfiguration from '../../routeConfiguration';
import { pathByRouteName } from '../../util/routes';
import { apiBaseUrl } from '../../util/api';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import config from '../../config';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import {
  isSignupDriverLicenceDuplicateError,
  isSignupDriverLicenceInvalidError,
  isSignupDriverLicenceLookupUnavailableError,
  isSignupDriverLicenceRateLimitedError,
  isSignupEmailTakenError,
  isSignupEmailDomainTypoError,
  signupEmailDomainTypoSuggestion,
  isTooManyEmailVerificationRequestsError,
} from '../../util/errors';
import { withViewport } from '../../util/contextHelpers';
import {
  Page,
  NamedLink,
  NamedRedirect,
  LinkTabNavHorizontal,
  ErrorToast,
  IconEmailSent,
  InlineTextButton,
  SocialLoginButton,
  IconClose,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  Modal,
  PrivacyPolicy,
  RentalTermsPremium,
  OwnerTermsPremium,
  SectionForOwnersFeatures,
  SectionPromoHero,
  TermsOfService,
} from '../../components';
import { ConfirmSignupForm, LoginForm, SignupForm } from '../../forms';
import { TopbarContainer } from '../../containers';
import { login, authenticationInProgress, signup, signupWithIdp } from '../../ducks/Auth.duck';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { sendVerificationEmail } from '../../ducks/user.duck';
import { manageDisableScrolling } from '../../ducks/UI.duck';
import { redirectIfInIframe } from '../../util/embeddedHelpers';

import css from './AuthenticationPage.module.css';
import { FacebookLogo, GoogleLogo } from './socialLoginLogos';
import FuelPromoTerms from '../../components/FuelPromoTerms/FuelPromoTerms';

export class AuthenticationPageComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tosModalOpen: false,
      fuelPromoTermsModalOpen: false,
      fbDisabledModalOpen: false,
      errorToastOpen: false,
      errorToastId: 0,
      authError: Cookies.get('st-autherror')
        ? JSON.parse(Cookies.get('st-autherror').replace('j:', ''))
        : null,
      authInfo: Cookies.get('st-authinfo')
        ? JSON.parse(Cookies.get('st-authinfo').replace('j:', ''))
        : null,
    };
    this.loginInitialValues = this.getLoginInitialValues();
    this.redirectFrom = this.getRedirectFrom(props, this.state.authInfo);
  }

  componentDidMount() {
    // If we're in an iframe, redirect to full authentication page
    // This ensures proper cookie handling and user experience
    if (redirectIfInIframe()) {
      return;
    }
    // Remove the autherror cookie once the content is saved to state
    // because we don't want to show the error message e.g. after page refresh
    Cookies.remove('st-autherror');
  }

  componentDidUpdate(prevProps) {
    if (!this.redirectFrom) {
      const nextFrom = this.getRedirectFrom(this.props);
      if (nextFrom) {
        this.redirectFrom = nextFrom;
      }
    }

    const { signupError, confirmError } = this.props;
    const hasNewSignupError = !!signupError && signupError !== prevProps.signupError;
    const hasNewConfirmError = !!confirmError && confirmError !== prevProps.confirmError;
    if (hasNewSignupError || hasNewConfirmError) {
      this.setState(prevState => ({
        errorToastOpen: true,
        errorToastId: prevState.errorToastId + 1,
      }));
    }
  }

  getRememberedEmail() {
    return Cookies.get('st-remembered-email') || '';
  }

  getRedirectFrom(props, authInfoOverride = null) {
    const { location } = props;
    const locationFrom = location.state && location.state.from ? location.state.from : null;
    const authInfo = authInfoOverride || this.state?.authInfo;
    const authinfoFrom = authInfo && authInfo.from ? authInfo.from : null;

    return locationFrom ? locationFrom : authinfoFrom ? authinfoFrom : null;
  }

  getLoginInitialValues() {
    const rememberedEmail = this.getRememberedEmail();
    const rememberMeEnabled = config.custom.featureToggles[config.env]?.rememberMe;

    return {
      email: rememberedEmail,
      rememberMe: rememberMeEnabled && rememberedEmail ? ['rememberMe'] : [],
    };
  }

  handleLoginSubmit = values => {
    const { email, password, rememberMe } = values;

    const isChecked = Array.isArray(rememberMe) && rememberMe.includes('rememberMe');

    if (isChecked) {
      // Set cookie with email (expires in 1 year) - checkbox was checked
      Cookies.set('st-remembered-email', email, { expires: 365 });
    } else {
      Cookies.remove('st-remembered-email');
    }

    return this.props.submitLogin({ email, password });
  };

  render() {
    const {
      authInProgress,
      currentUser,
      intl,
      isAuthenticated,
      location,
      loginError,
      scrollingDisabled,
      signupError,
      submitLogin,
      submitSignup,
      confirmError,
      submitSingupWithIdp,
      tab,
      promoCode,
      referrerName,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      onResendVerificationEmail,
      onManageDisableScrolling,
      viewport,
    } = this.props;

    const isConfirm = tab === 'confirm';
    const isLogin = tab === 'login';
    const from = this.redirectFrom;

    const user = ensureCurrentUser(currentUser);
    const currentUserLoaded = !!user.id;

    // We only want to show the email verification dialog in the signup
    // tab if the user isn't being redirected somewhere else
    // (i.e. `from` is present). We must also check the `emailVerified`
    // flag only when the current user is fully loaded.
    const showEmailVerification = !isLogin && currentUserLoaded && !user.attributes.emailVerified;

    // Already authenticated, redirect away from auth page
    if (isAuthenticated && from) {
      return <Redirect to={from} />;
    } else if (
      isAuthenticated &&
      currentUserLoaded &&
      !showEmailVerification &&
      !promoCode &&
      !referrerName
    ) {
      return <NamedRedirect name="LandingPage" />;
    }

    const loginErrorMessage = (
      <div className={css.error}>
        <FormattedMessage id="AuthenticationPage.loginFailed" />
      </div>
    );

    const driverLicenceDuplicateMessage = (
      <FormattedMessage
        id="AuthenticationPage.signupFailedDriverLicenceDuplicate"
        values={{
          loginLink: (
            <NamedLink name="LoginPage" className={css.errorLink}>
              <FormattedMessage id="AuthenticationPage.signupFailedDriverLicenceDuplicateLogin" />
            </NamedLink>
          ),
          passwordRecoveryLink: (
            <NamedLink name="PasswordRecoveryPage" className={css.errorLink}>
              <FormattedMessage id="AuthenticationPage.signupFailedDriverLicenceDuplicatePasswordReset" />
            </NamedLink>
          ),
          emailLink: (
            <a href="mailto:hello@localtrailerhire.com.au" className={css.errorLink}>
              hello@localtrailerhire.com.au
            </a>
          ),
        }}
      />
    );

    const getSignupOrConfirmErrorContent = error => {
      if (isSignupDriverLicenceDuplicateError(error)) {
        return driverLicenceDuplicateMessage;
      }
      if (isSignupDriverLicenceInvalidError(error)) {
        return <FormattedMessage id="AuthenticationPage.signupFailedDriverLicenceInvalid" />;
      }
      if (isSignupDriverLicenceLookupUnavailableError(error)) {
        return (
          <FormattedMessage id="AuthenticationPage.signupFailedDriverLicenceLookupUnavailable" />
        );
      }
      if (isSignupDriverLicenceRateLimitedError(error)) {
        return <FormattedMessage id="AuthenticationPage.signupFailedDriverLicenceRateLimited" />;
      }
      if (isSignupEmailTakenError(error)) {
        return <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />;
      }
      if (isSignupEmailDomainTypoError(error)) {
        const suggestedEmail = signupEmailDomainTypoSuggestion(error);
        return suggestedEmail ? (
          <FormattedMessage
            id="AuthenticationPage.signupFailedEmailDomainTypo"
            values={{ email: suggestedEmail }}
          />
        ) : (
          <FormattedMessage id="EmailTypoSuggestion.domainMistyped" />
        );
      }
      return <FormattedMessage id="AuthenticationPage.signupFailed" />;
    };

    const signupErrorMessage = (
      <div className={css.error}>{getSignupOrConfirmErrorContent(signupError)}</div>
    );

    const confirmErrorMessage = confirmError ? (
      <div className={css.error}>{getSignupOrConfirmErrorContent(confirmError)}</div>
    ) : null;

    // eslint-disable-next-line no-confusing-arrow
    const errorMessage = (error, message) => (error ? message : null);
    const loginOrSignupError = isLogin
      ? errorMessage(loginError, loginErrorMessage)
      : errorMessage(signupError, signupErrorMessage);

    const toastError = isConfirm ? confirmError : isLogin ? null : signupError;
    const errorToast =
      toastError && this.state.errorToastOpen ? (
        <ErrorToast
          key={this.state.errorToastId}
          onClose={() => this.setState({ errorToastOpen: false })}
        >
          {getSignupOrConfirmErrorContent(toastError)}
        </ErrorToast>
      ) : null;

    const fromState = { state: from ? { from } : null };

    const tabs = [
      {
        text: (
          <h1 className={css.tab}>
            <FormattedMessage id="AuthenticationPage.signupLinkText" />
          </h1>
        ),
        selected: !isLogin,
        linkProps: {
          name: 'SignupPage',
          to: fromState,
        },
      },
      {
        text: (
          <h1 className={css.tab}>
            <FormattedMessage id="AuthenticationPage.loginLinkText" />
          </h1>
        ),
        selected: isLogin,
        linkProps: {
          name: 'LoginPage',
          to: fromState,
        },
      },
    ];

    const handleSubmitSignup = values => {
      const { fname, lname, residentialAddress, ...rest } = values;
      const params = {
        firstName: fname.trim(),
        lastName: lname.trim(),
        residentialAddress: residentialAddress.selectedPlace.address,
        signupMethod: 'password',
        ...rest,
      };

      submitSignup(params);
    };

    const handleSubmitConfirm = values => {
      const { idpToken, email, firstName, lastName, idpId } = this.state.authInfo;
      const {
        email: newEmail,
        firstName: newFirstName,
        lastName: newLastName,
        residentialAddress,
        ...rest
      } = values;

      // Pass email, firstName or lastName to Flex API only if user has edited them
      // If not edited, the server will extract them from the IDP token
      const authParams = {
        ...(newEmail !== email && { email: newEmail }),
        ...(newFirstName !== firstName && { firstName: newFirstName }),
        ...(newLastName !== lastName && { lastName: newLastName }),
      };

      // If the confirm form has any additional values, pass them forward as user's protected data
      const protectedData = !isEmpty(rest)
        ? {
            ...rest,
            signupMethod: idpId,
            residentialAddress: residentialAddress.selectedPlace.address,
          }
        : null;

      submitSingupWithIdp({
        idpToken,
        idpId,
        ...authParams,
        ...(!!protectedData && { protectedData }),
      });
    };

    const getDefaultRoutes = (promoCode, referrerName) => {
      const routes = routeConfiguration();
      const baseUrl = apiBaseUrl();

      // Route where the user should be returned after authentication
      // This is used e.g. with EditListingPage and ListingPage
      const fromParam = from ? `from=${from}` : '';

      // Default route where user is returned after successfull authentication
      const defaultReturn = pathByRouteName('LandingPage', routes);
      const defaultReturnParam = defaultReturn ? `&defaultReturn=${defaultReturn}` : '';

      // Route for confirming user data before creating a new user
      const defaultConfirmParam = confirmParamForPromoCodeOrReferral(
        promoCode,
        referrerName,
        routes
      );

      return { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam };
    };

    const confirmParamForPromoCodeOrReferral = (promoCode, referrerName, routes) => {
      if (promoCode === 'FUEL') {
        const confirm = pathByRouteName('ConfirmPageWithFuelPromo', routes);
        return confirm ? `&defaultConfirm=${confirm}` : '';
      } else if (referrerName === 'crystal') {
        const confirm = pathByRouteName('ConfirmPageWithReferral', routes);
        return confirm ? `&defaultConfirm=${confirm}` : '';
      } else {
        const defaultConfirm = pathByRouteName('ConfirmPage', routes);
        return defaultConfirm ? `&defaultConfirm=${defaultConfirm}` : '';
      }
    };

    const authWithFacebook = (promoCode, referrerName) => {
      const defaultRoutes = getDefaultRoutes(promoCode, referrerName);
      const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;
      window.location.href = `${baseUrl}/api/auth/facebook?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
    };

    const authWithGoogle = (promoCode, referrerName) => {
      const defaultRoutes = getDefaultRoutes(promoCode, referrerName);
      const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;
      window.location.href = `${baseUrl}/api/auth/google?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
    };

    const idp = this.state.authInfo
      ? this.state.authInfo.idpId.replace(/^./, str => str.toUpperCase())
      : null;

    // Form for confirming information frm IdP (e.g. Facebook)
    // before new user is created to Flex
    const confirmForm = (
      <div className={css.content}>
        <h1 className={css.signupWithIdpTitle}>
          <FormattedMessage id="AuthenticationPage.confirmSignupWithIdpTitle" values={{ idp }} />
        </h1>

        <p className={css.confirmInfoText}>
          <FormattedMessage id="AuthenticationPage.confirmSignupInfoText" />
        </p>
        {confirmErrorMessage}
        <ConfirmSignupForm
          className={css.form}
          onSubmit={handleSubmitConfirm}
          inProgress={authInProgress}
          onOpenTermsOfService={() => this.setState({ tosModalOpen: true })}
          authInfo={this.state.authInfo}
          idp={idp}
          referrerName={referrerName}
        />
      </div>
    );

    // Social login buttons
    const showFacebookLogin = !!process.env.REACT_APP_FACEBOOK_APP_ID;
    const showGoogleLogin = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const showSocialLogins = (showFacebookLogin || showGoogleLogin) && !isAuthenticated;

    // Feature flag to temporarily disable Facebook auth
    const disableFacebookAuth = process.env.REACT_APP_DISABLE_FACEBOOK_AUTH === 'true';

    // Hide Facebook on signup when disabled; keep visible on login but intercept click
    const canShowFacebookButton = showFacebookLogin && (isLogin || !disableFacebookAuth);
    const facebookOnClick =
      disableFacebookAuth && isLogin
        ? () => this.setState({ fbDisabledModalOpen: true })
        : () => authWithFacebook(promoCode, referrerName);

    const facebookButtonText = isLogin ? (
      <FormattedMessage id="AuthenticationPage.loginWithFacebook" />
    ) : (
      <FormattedMessage id="AuthenticationPage.signupWithFacebook" />
    );

    const googleButtonText = isLogin ? (
      <FormattedMessage id="AuthenticationPage.loginWithGoogle" />
    ) : (
      <FormattedMessage id="AuthenticationPage.signupWithGoogle" />
    );

    const socialLoginButtonsMaybe = showSocialLogins ? (
      <div className={css.idpButtons}>
        {canShowFacebookButton ? (
          <div className={css.socialButtonWrapper}>
            <SocialLoginButton onClick={facebookOnClick}>
              <span className={css.buttonIcon}>{FacebookLogo}</span>
              {facebookButtonText}
            </SocialLoginButton>
          </div>
        ) : null}

        {showGoogleLogin ? (
          <div className={css.socialButtonWrapper}>
            <SocialLoginButton onClick={() => authWithGoogle(promoCode, referrerName)}>
              <span className={css.buttonIcon}>{GoogleLogo}</span>
              {googleButtonText}
            </SocialLoginButton>
          </div>
        ) : null}
        <div className={css.socialButtonsOr}>
          <span className={css.socialButtonsOrText}>
            <FormattedMessage id="AuthenticationPage.or" />
          </span>
        </div>
      </div>
    ) : null;

    const promoHeading = () => {
      if (referrerName) {
        return isAuthenticated ? null : <FormattedMessage id="AuthenticationPage.signupLinkText" />;
      }
      return isAuthenticated ? (
        <FormattedMessage id="AuthenticationPage.authenticatedFuelPromo" />
      ) : (
        <FormattedMessage id="AuthenticationPage.signupFuelPromo" />
      );
    };

    const createListingLink = (
      <div className={css.createListingLinkContainer}>
        <NamedLink className={css.createListingLink} name="NewListingPage">
          <span className={css.createListing}>
            <FormattedMessage id={'AuthenticationPage.createListing'} />
          </span>
        </NamedLink>
      </div>
    );
    const loginLink = (
      <NamedLink name="LoginPage" className={css.signupLoginLink}>
        <FormattedMessage id="SignupForm.loginLinkText" />
      </NamedLink>
    );
    const signupLoginPrompt = (
      <p className={css.signupLoginPrompt}>
        <FormattedMessage id="SignupForm.loginPrompt" values={{ loginLink }} />
      </p>
    );
    const signupLink = (
      <NamedLink name="SignupPage" className={css.signupLoginLink}>
        <FormattedMessage id="LoginForm.createAccountLinkText" />
      </NamedLink>
    );
    const loginSignupPrompt = (
      <p className={css.signupLoginPrompt}>
        <FormattedMessage id="LoginForm.createAccountPrompt" values={{ signupLink }} />
      </p>
    );
    // Tabs for SignupForm and LoginForm
    const authenticationForms = (
      <div className={css.content}>
        {promoCode || referrerName ? (
          <h2>{promoHeading()}</h2>
        ) : (
          <LinkTabNavHorizontal className={css.tabs} tabs={tabs} />
        )}
        {loginOrSignupError}

        {isLogin ? loginSignupPrompt : signupLoginPrompt}
        {promoCode ? null : socialLoginButtonsMaybe}

        {isLogin ? (
          <LoginForm
            className={css.loginForm}
            onSubmit={this.handleLoginSubmit}
            inProgress={authInProgress}
            initialValues={this.loginInitialValues}
          />
        ) : isAuthenticated ? (
          <React.Fragment>
            {promoCode ? (
              <p>
                It looks like you're already logged in. When you add your first trailer, just
                include the word "fuel" in the listing title to ensure you receive your fuel card
                for the first hire.
              </p>
            ) : null}
            {createListingLink}
            <p>
              Already have a trailer listed? Check out our{' '}
              <NamedLink name="MusteringPage">
                referral program, the Great Australian Trailer Muster
              </NamedLink>{' '}
              to see how you can earn cash by referring other trailer owners.
            </p>
          </React.Fragment>
        ) : (
          <SignupForm
            className={css.signupForm}
            onSubmit={handleSubmitSignup}
            inProgress={authInProgress}
            onOpenTermsOfService={() => this.setState({ tosModalOpen: true })}
            onOpenFuelPromoTerms={() => this.setState({ fuelPromoTermsModalOpen: true })}
            promoCode={promoCode}
            referrerName={referrerName}
          />
        )}
      </div>
    );

    const formContent = isConfirm ? confirmForm : authenticationForms;

    const name = user.attributes.profile.firstName;
    const email = <span className={css.email}>{user.attributes.email}</span>;

    const verifyCloseLink = from ? (
      <Link className={css.verifyClose} to={from}>
        <span className={css.closeText} data-testid="verify-email-close">
          <FormattedMessage id="AuthenticationPage.verifyEmailClose" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Link>
    ) : (
      <NamedLink className={css.verifyClose} name="ProfileSettingsPage">
        <span className={css.closeText} data-testid="verify-email-close">
          <FormattedMessage id="AuthenticationPage.verifyEmailClose" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </NamedLink>
    );

    const resendEmailLink = (
      <InlineTextButton rootClassName={css.modalHelperLink} onClick={onResendVerificationEmail}>
        <FormattedMessage id="AuthenticationPage.resendEmailLinkText" />
      </InlineTextButton>
    );
    const fixEmailLink = (
      <NamedLink className={css.modalHelperLink} name="ContactDetailsPage">
        <FormattedMessage id="AuthenticationPage.fixEmailLinkText" />
      </NamedLink>
    );

    const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
      sendVerificationEmailError
    )
      ? 'AuthenticationPage.resendFailedTooManyRequests'
      : 'AuthenticationPage.resendFailed';
    const resendErrorMessage = sendVerificationEmailError ? (
      <p className={css.error}>
        <FormattedMessage id={resendErrorTranslationId} />
      </p>
    ) : null;

    const emailVerificationContent = (
      <div className={css.content} data-testid="email-verification-content">
        {verifyCloseLink}
        <IconEmailSent className={css.modalIcon} />
        <h1 className={css.modalTitle}>
          <FormattedMessage id="AuthenticationPage.verifyEmailTitle" values={{ name }} />
        </h1>
        <p className={css.modalMessage}>
          <FormattedMessage id="AuthenticationPage.verifyEmailText" values={{ email }} />
        </p>
        <p className={css.modalMessage}>
          <FormattedMessage id="AuthenticationPage.verifyEmailCheckSpam" />
        </p>
        {resendErrorMessage}

        <div className={css.bottomWrapper}>
          <p className={css.modalHelperText}>
            {sendVerificationEmailInProgress ? (
              <FormattedMessage id="AuthenticationPage.sendingEmail" />
            ) : (
              <FormattedMessage id="AuthenticationPage.resendEmail" values={{ resendEmailLink }} />
            )}
          </p>
          <p className={css.modalHelperText}>
            <FormattedMessage id="AuthenticationPage.fixEmail" values={{ fixEmailLink }} />
          </p>
        </div>
      </div>
    );

    const siteTitle = config.siteTitle;
    const schemaTitle = isLogin
      ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleLogin' }, { siteTitle })
      : intl.formatMessage({ id: 'AuthenticationPage.schemaTitleSignup' }, { siteTitle });

    const topbarClasses = classNames({
      [css.hideOnMobile]: showEmailVerification,
    });

    const promoHeader = (
      <React.Fragment>
        <div className={css.heroContainer}>
          <SectionPromoHero className={css.hero} referrerName={referrerName} />
        </div>
        <ul className={css.sections}>
          <li className={css.section}>
            <div className={css.sectionContent}>
              <SectionForOwnersFeatures viewport={viewport} />
              <div className={css.learnAboutListingLinkContainer}>
                <NamedLink className={css.learnAboutListingLink} name="ForOwnersPage">
                  <span>
                    <FormattedMessage id="SectionPromoHero.learnAboutListingLink" />
                  </span>
                </NamedLink>
              </div>
            </div>
          </li>
        </ul>
      </React.Fragment>
    );

    return (
      <Page
        title={schemaTitle}
        scrollingDisabled={scrollingDisabled}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'WebPage',
          name: schemaTitle,
        }}
      >
        <LayoutSingleColumn>
          <LayoutWrapperTopbar>
            <TopbarContainer className={topbarClasses} />
          </LayoutWrapperTopbar>
          <LayoutWrapperMain className={css.layoutWrapperMain}>
            <div>
              {promoCode || referrerName ? promoHeader : null}
              <div className={css.root} id="form">
                {showEmailVerification ? emailVerificationContent : formContent}
              </div>
            </div>
            {showEmailVerification ? null : errorToast}
            <Modal
              id="AuthenticationPage.tos"
              isOpen={this.state.tosModalOpen}
              onClose={() => this.setState({ tosModalOpen: false })}
              usePortal
              onManageDisableScrolling={onManageDisableScrolling}
            >
              <div className={css.termsWrapper}>
                <h2 className={css.termsHeading}>
                  <FormattedMessage id="AuthenticationPage.rentalTermsHeading" />
                </h2>
                <RentalTermsPremium />
                <h2 className={css.termsHeading}>
                  <FormattedMessage id="AuthenticationPage.ownerTermsHeading" />
                </h2>
                <OwnerTermsPremium />
                <h2 className={css.termsHeading}>
                  <FormattedMessage id="AuthenticationPage.privacyPolicyHeading" />
                </h2>
                <PrivacyPolicy />

                <h2 className={css.termsHeading}>
                  <FormattedMessage id="AuthenticationPage.termsOfServiceHeading" />
                </h2>
                <TermsOfService />
              </div>
            </Modal>
            <Modal
              id="AuthenticationPage.facebookDisabled"
              isOpen={this.state.fbDisabledModalOpen}
              onClose={() => this.setState({ fbDisabledModalOpen: false })}
              usePortal
              onManageDisableScrolling={onManageDisableScrolling}
            >
              <div className={css.content}>
                <h2 className={css.modalTitle}>
                  <FormattedMessage id="AuthenticationPage.facebookDisabledTitle" />
                </h2>
                <p className={css.modalMessage}>
                  {/* Link to password recovery page */}
                  <FormattedMessage
                    id="AuthenticationPage.facebookDisabledMessage"
                    values={{
                      resetPasswordLink: (
                        <NamedLink name="PasswordRecoveryPage">reset your password</NamedLink>
                      ),
                    }}
                  />
                </p>
              </div>
            </Modal>
            <Modal
              id="AuthenticationPage.fuelPromoTerms"
              isOpen={this.state.fuelPromoTermsModalOpen}
              onClose={() => this.setState({ fuelPromoTermsModalOpen: false })}
              usePortal
              onManageDisableScrolling={onManageDisableScrolling}
            >
              <div className={css.termsWrapper}>
                <h2 className={css.termsHeading}>Terms and Conditions</h2>
                <FuelPromoTerms />
              </div>
            </Modal>
          </LayoutWrapperMain>
          <LayoutWrapperFooter>
            <Footer />
          </LayoutWrapperFooter>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

AuthenticationPageComponent.defaultProps = {
  currentUser: null,
  loginError: null,
  signupError: null,
  confirmError: null,
  tab: 'signup',
  sendVerificationEmailError: null,
  showSocialLoginsForTests: false,
};

const { bool, func, object, oneOf, shape } = PropTypes;

AuthenticationPageComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  isAuthenticated: bool.isRequired,
  loginError: propTypes.error,
  scrollingDisabled: bool.isRequired,
  signupError: propTypes.error,
  confirmError: propTypes.error,

  submitLogin: func.isRequired,
  submitSignup: func.isRequired,
  tab: oneOf(['login', 'signup', 'confirm']),

  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  onManageDisableScrolling: func.isRequired,

  // from withRouter
  location: shape({ state: object }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, loginError, signupError, confirmError } = state.Auth;
  const { currentUser, sendVerificationEmailInProgress, sendVerificationEmailError } = state.user;
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    isAuthenticated,
    loginError,
    scrollingDisabled: isScrollingDisabled(state),
    signupError,
    confirmError,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  };
};

const mapDispatchToProps = dispatch => ({
  submitLogin: ({ email, password }) => dispatch(login(email, password)),
  submitSignup: params => dispatch(signup(params)),
  submitSingupWithIdp: params => dispatch(signupWithIdp(params)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AuthenticationPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport,
  injectIntl
)(AuthenticationPageComponent);

export default AuthenticationPage;
